import React, { useMemo } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  formatSnakeCaseToGeneral,
  Inscription,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { DEFAULT_DATE_TIME_FORMAT } from '@/common/constants';
import { unCamelCase } from '@/utils';

import { EllipsisText } from '../../ActivityLog.styles';
import { ActivityLog } from '../useGetActivityLogs.query';

import Changeset from './Changeset';

const unformattedFields = ['API'];

const Wrapper = styled(Box)`
  border: 1px solid ${({ theme }) => theme.colors.border2};
`;

interface Props {
  data: ActivityLog;
  isExpanded: boolean;
  setExpandedLogId: (val: string) => void;
  isTwoColumnMode?: boolean;
}

const LogItem: React.FC<Props> = ({ data, isExpanded, setExpandedLogId, isTwoColumnMode }) => {
  const source = useMemo(() => {
    if (unformattedFields.some((field) => data.updateSource === field)) {
      return data.updateSource;
    }

    return formatSnakeCaseToGeneral(data.updateSource);
  }, [data.updateSource]);

  return (
    <Wrapper
      data-testid={`ActivityLog_row_${data.id}`}
      background="canvas"
      pad={{ vertical: 'spacing18', horizontal: 'spacing24' }}
      round="8px"
    >
      <Box
        direction="row"
        align="center"
        onClick={() => setExpandedLogId(isExpanded ? '' : data.id)}
        data-testid={`ActivityLog_row_${data.id}_toggle`}
      >
        <Box width="3%" justify="center" align="center" data-testid={`ActivityLog_row_${data.id}_expand`}>
          {isExpanded ? (
            <ChevronUpIcon color="textTitle" data-testid={`ActivityLog_row_${data.id}_expand-icon`} />
          ) : (
            <ChevronDownIcon color="textTitle" data-testid={`ActivityLog_row_${data.id}_expand-icon`} />
          )}
        </Box>
        <Box width="22%" pad={{ left: 'spacing16' }} data-testid={`ActivityLog_row_${data.id}_date`}>
          <Inscription
            lineHeight="22px"
            size="16px"
            color="textBody"
            data-testid={`ActivityLog_row_${data.id}_date-value`}
          >
            {dayjs(data.changedAt).format(DEFAULT_DATE_TIME_FORMAT)}
          </Inscription>
        </Box>
        <Box width="22%" pad={{ left: 'spacing16' }} data-testid={`ActivityLog_row_${data.id}_entity`}>
          <Inscription
            weight="bold"
            lineHeight="20px"
            color="textBody"
            data-testid={`ActivityLog_row_${data.id}_entity-value`}
          >
            {unCamelCase(data.entityName)}
          </Inscription>
        </Box>
        <Box
          width="27%"
          pad={{ left: 'spacing16' }}
          direction="row"
          align="center"
          data-testid={`ActivityLog_row_${data.id}_user`}
        >
          <EllipsisText
            color="textBody"
            lineHeight="20px"
            title={data.changedByName}
            style={{ maxWidth: '170px' }}
            data-testid={`ActivityLog_row_${data.id}_user-value`}
          >
            {data.changedByName}
          </EllipsisText>
          <Box
            round="xxsmall"
            background="nonClaimBg"
            height="18px"
            justify="center"
            align="center"
            pad={{ horizontal: '3px' }}
            margin={{ left: '7px' }}
            data-testid={`ActivityLog_row_${data.id}_user-type`}
          >
            <Inscription
              size="10px"
              lineHeight="18px"
              weight={500}
              color="textBody"
              style={{ letterSpacing: '0.4px' }}
              data-testid={`ActivityLog_row_${data.id}_user-type-value`}
            >
              {capitalize(formatSnakeCaseToGeneral(data.actorType))}
            </Inscription>
          </Box>
        </Box>
        <Box width="18%" pad={{ left: 'spacing16' }} data-testid={`ActivityLog_row_${data.id}_source`}>
          <Inscription
            color="textBody"
            lineHeight="20px"
            data-testid={`ActivityLog_row_${data.id}_source-value`}
          >
            {source}
          </Inscription>
        </Box>
        <Box width="8%" pad={{ left: 'spacing16' }} data-testid={`ActivityLog_row_${data.id}_details`}>
          <Inscription
            lineHeight="20px"
            color="textSecondary"
            data-testid={`ActivityLog_row_${data.id}_details-value`}
          >
            {data.changesCount}
          </Inscription>
        </Box>
      </Box>

      {isExpanded ? (
        <Box
          margin={{ top: 'spacing8', bottom: 'spacing6' }}
          data-testid={`ActivityLog_row_${data.id}_expanded`}
        >
          <Changeset
            id={data.id}
            type={data.actionType}
            source={source}
            planName={data.planName}
            sourceEntryPoint={data.sourceEntryPoint}
            isTwoColumnMode={isTwoColumnMode}
          />
        </Box>
      ) : null}
    </Wrapper>
  );
};

export default LogItem;
